import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/components/atoms/primitives/utils';

/**
 * WEB-1001 — the design-system button (DS 1.9.0 `.a-btn`).
 *
 * Geometry is the "Atoms v1 control-size ruling — DS 1.9.0": sm 32 / md 40 /
 * lg 48 visual on every pointer, plus the hero-only xl 56 pill. On coarse
 * pointers an invisible `::before` grows sm/md to a 44×44 hit box. All paint
 * lives in `styles/components/atoms.css`; this component only maps props to
 * classes, so a call site never re-states a size in Tailwind.
 */
export type ButtonVariant = 'cta' | 'accent' | 'ghost' | 'glass' | 'text' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonBaseProps extends Omit<React.ComponentPropsWithoutRef<'button'>, 'type'> {
  type?: 'button' | 'submit' | 'reset';
  /** Square icon-only button (32 / 40 / 48 / 56). Give it an `aria-label`. */
  icon?: boolean;
  /** Pill radius at any size. */
  round?: boolean;
  /** Full-width. */
  block?: boolean;
  /**
   * Busy (`aria-busy`): hides the label, shows a spinner and blocks pointer
   * input. The button is also disabled so a form cannot double-submit.
   */
  busy?: boolean;
  /** Render the single child (e.g. a Next `Link`) with the button's classes. */
  asChild?: boolean;
}

interface ButtonScaleProps extends ButtonBaseProps {
  variant?: ButtonVariant;
  /** @default 'md' */
  size?: Exclude<ButtonSize, 'xl'>;
  toggle?: false;
  pressed?: never;
}

/** xl is hero / marketing only, one per view, `cta` or `accent` only. */
interface ButtonHeroProps extends ButtonBaseProps {
  variant: 'cta' | 'accent';
  size: 'xl';
  toggle?: false;
  pressed?: never;
}

/** Toggle (follow, save): state is `aria-pressed`, never a class. */
interface ButtonToggleProps extends ButtonBaseProps {
  variant?: never;
  size?: Exclude<ButtonSize, 'xl'>;
  toggle: true;
  pressed: boolean;
}

export type ButtonProps = ButtonScaleProps | ButtonHeroProps | ButtonToggleProps;

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: boolean;
  round?: boolean;
  block?: boolean;
  toggle?: boolean;
}

/** The class list for a `.a-btn`. Exported for the rare element a component can't wrap. */
export function buttonClassName({
  variant,
  size = 'md',
  icon = false,
  round = false,
  block = false,
  toggle = false,
}: ButtonClassOptions = {}): string {
  return cn(
    'a-btn',
    variant && `a-btn--${variant}`,
    size !== 'md' && `a-btn--${size}`,
    icon && 'a-btn--icon',
    round && 'a-btn--round',
    block && 'a-btn--block',
    toggle && 'a-btn--toggle',
  );
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    size,
    icon,
    round,
    block,
    toggle,
    pressed,
    busy = false,
    asChild = false,
    disabled,
    type,
    className,
    ...props
  },
  ref,
) {
  const classes = cn(buttonClassName({ variant, size, icon, round, block, toggle }), className);
  const stateProps = {
    'aria-busy': busy || undefined,
    'aria-pressed': toggle ? pressed : undefined,
  };

  if (asChild) {
    // A slotted `<a>` / Next `<Link>` has no `disabled`: when busy or disabled,
    // take it out of the tab order, mark it `aria-disabled`, and swallow the
    // click in the CAPTURE phase — Radix `Slot` runs the child's own onClick
    // before a bubbling Slot handler, which would be too late to stop a Link.
    const inert = disabled || busy;
    return (
      <Slot
        ref={ref}
        className={classes}
        {...stateProps}
        {...props}
        {...(inert
          ? {
              'aria-disabled': true,
              tabIndex: -1,
              onClickCapture: (event: React.MouseEvent) => {
                event.preventDefault();
                event.stopPropagation();
              },
            }
          : {})}
      />
    );
  }

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || busy}
      {...stateProps}
      {...props}
    />
  );
});
